import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';
import { Wallet, CreditCard, Phone, ArrowDownRight, ArrowUpRight, RefreshCw, Clock, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Billing() {
  const { token, user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [allWallets, setAllWallets] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topupLoading, setTopupLoading] = useState(null);
  const [activateLoading, setActivateLoading] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [wRes, pRes, tRes, pmRes] = await Promise.all([
        fetch(`${API}/api/billing/wallet`, { headers }),
        fetch(`${API}/api/billing/pricing`, { headers }),
        fetch(`${API}/api/billing/transactions`, { headers }),
        fetch(`${API}/api/billing/payments`, { headers }),
      ]);
      setWallet(await wRes.json());
      setPricing(await pRes.json());
      const tData = await tRes.json();
      setTransactions(Array.isArray(tData) ? tData : []);
      const pmData = await pmRes.json();
      setPayments(Array.isArray(pmData) ? pmData : []);

      if (user?.role === 'superadmin') {
        const awRes = await fetch(`${API}/api/billing/all-wallets`, { headers });
        if (awRes.ok) setAllWallets(await awRes.json());
      }

      if (['superadmin', 'partner'].includes(user?.role)) {
        const sRes = await fetch(`${API}/api/billing/subscriptions`, { headers });
        if (sRes.ok) {
          const sData = await sRes.json();
          setSubscriptions(Array.isArray(sData) ? sData : []);
        }
      }
    } catch { toast.error('Errore caricamento dati'); }
    finally { setLoading(false); }
  }, [token, user]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Check for topup return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const topup = params.get('topup');
    if (sessionId && topup === 'success') {
      pollPaymentStatus(sessionId);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) return;
    try {
      const res = await fetch(`${API}/api/billing/topup/status/${sessionId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.payment_status === 'paid') {
        toast.success(`Ricarica di €${data.amount} completata!`);
        fetchAll();
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch { /* retry */ }
  };

  const handleTopup = async (packageId) => {
    setTopupLoading(packageId);
    try {
      const res = await fetch(`${API}/api/billing/topup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ package_id: packageId, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.detail || 'Errore nella creazione del pagamento');
      }
    } catch { toast.error('Errore di rete'); }
    finally { setTopupLoading(null); }
  };

  const handleActivate = async (clinicId, clinicName) => {
    if (!window.confirm(`Attivare "${clinicName}"? Verra addebitato €300 (canone annuale) dal wallet.`)) return;
    setActivateLoading(clinicId);
    try {
      const res = await fetch(`${API}/api/billing/activate-clinic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clinic_id: clinicId }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Clinica attivata! Addebitato €${data.charged}. Valida fino a ${new Date(data.annual_valid_until).toLocaleDateString('it-IT')}`);
        fetchAll();
      } else {
        toast.error(data.detail || 'Errore');
      }
    } catch { toast.error('Errore di rete'); }
    finally { setActivateLoading(null); }
  };

  const handleRenew = async (clinicId, clinicName) => {
    if (!window.confirm(`Rinnovare "${clinicName}"? Verra addebitato €300 dal wallet.`)) return;
    setActivateLoading(clinicId);
    try {
      const res = await fetch(`${API}/api/billing/renew-clinic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clinic_id: clinicId }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Rinnovo completato! Valida fino a ${new Date(data.valid_until).toLocaleDateString('it-IT')}`);
        fetchAll();
      } else {
        toast.error(data.detail || 'Errore');
      }
    } catch { toast.error('Errore di rete'); }
    finally { setActivateLoading(null); }
  };

  if (loading) return (
    <Layout><div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" /></div></Layout>
  );

  return (
    <Layout>
      <div className="content-area" data-testid="billing-page">
        <div className="max-w-5xl mx-auto space-y-6">

          {/* Header */}
          <div>
            <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>Billing & Crediti</h1>
            <p className="text-sm text-slate-500 mt-1">Gestione wallet, ricariche e consumi</p>
          </div>

          {/* Wallet Card */}
          <div className="bg-gradient-to-br from-sky-600 to-indigo-700 rounded-2xl p-6 text-white" data-testid="wallet-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sky-200 text-sm">Saldo Wallet</p>
                <p className="text-4xl font-bold mt-1">€{wallet?.balance?.toFixed(2) || '0.00'}</p>
              </div>
              <Wallet className="w-12 h-12 text-sky-300 opacity-60" />
            </div>
            <div className="flex gap-6 mt-4 text-sm">
              <div>
                <span className="text-sky-300">Totale ricaricato</span>
                <p className="font-semibold">€{wallet?.total_topped_up?.toFixed(2) || '0.00'}</p>
              </div>
              <div>
                <span className="text-sky-300">Totale speso</span>
                <p className="font-semibold">€{wallet?.total_spent?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </div>

          {/* Top-up Packages */}
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="topup-section">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-sky-600" /> Ricarica Wallet
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {pricing?.packages && Object.entries(pricing.packages).map(([id, pkg]) => (
                <button key={id} onClick={() => handleTopup(id)} disabled={topupLoading === id}
                  className="p-4 border-2 border-slate-200 rounded-xl hover:border-sky-500 hover:bg-sky-50 transition-all text-center disabled:opacity-50" data-testid={`topup-${id}`}>
                  <p className="text-2xl font-bold text-slate-800">€{pkg.amount.toFixed(0)}</p>
                  <p className="text-xs text-slate-500 mt-1 capitalize">{id}</p>
                  {topupLoading === id && <div className="w-4 h-4 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mt-2" />}
                </button>
              ))}
            </div>
          </div>

          {/* Pricing Info */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="font-semibold text-slate-800 mb-4">Listino Prezzi</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 bg-emerald-50 rounded-lg text-center">
                <Building2 className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <p className="text-lg font-bold text-slate-800">Gratuito</p>
                <p className="text-xs text-slate-500">Setup clinica + 7gg prova</p>
              </div>
              <div className="p-4 bg-sky-50 rounded-lg text-center">
                <RefreshCw className="w-6 h-6 text-sky-600 mx-auto mb-2" />
                <p className="text-lg font-bold text-slate-800">€{pricing?.pricing?.clinic_annual || 300}/anno</p>
                <p className="text-xs text-slate-500">Canone annuale per clinica</p>
              </div>
              <div className="p-4 bg-violet-50 rounded-lg text-center">
                <Phone className="w-6 h-6 text-violet-600 mx-auto mb-2" />
                <p className="text-lg font-bold text-slate-800">€{pricing?.pricing?.voice_per_minute || 0.10}/min</p>
                <p className="text-xs text-slate-500">Traffico voce AI</p>
              </div>
            </div>
          </div>

          {/* Superadmin: All Wallets */}
          {user?.role === 'superadmin' && allWallets.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="all-wallets">
              <h2 className="font-semibold text-slate-800 mb-4">Wallet Partner</h2>
              <div className="space-y-2">
                {allWallets.map((w, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <span className="font-medium text-slate-800">{w.user_name || w.user_email}</span>
                      <span className="text-xs text-slate-400 ml-2">{w.role}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-emerald-600 font-medium">€{w.balance.toFixed(2)}</span>
                      <span className="text-slate-400">Ricaricato: €{w.total_topped_up.toFixed(2)}</span>
                      <span className="text-slate-400">Speso: €{w.total_spent.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Subscriptions */}
          {subscriptions.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="subscriptions-section">
              <h2 className="font-semibold text-slate-800 flex items-center gap-2 mb-4">
                <Building2 className="w-5 h-5 text-sky-600" /> Abbonamenti Cliniche
              </h2>
              <div className="space-y-3">
                {subscriptions.map((s, i) => {
                  const isExpired = s.annual_paid_until && new Date(s.annual_paid_until) < new Date();
                  const isPaid = s.annual_paid_until && !isExpired;
                  const isTrial = s.trial_mode;
                  const trialExpired = isTrial && s.trial_days_left === 0;
                  return (
                    <div key={i} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${isPaid ? 'bg-emerald-500' : isTrial && !trialExpired ? 'bg-amber-500 animate-pulse' : 'bg-red-500'}`} />
                        <div>
                          <p className="font-medium text-slate-800">{s.clinic_name}</p>
                          <div className="flex gap-3 mt-0.5">
                            {isTrial && !trialExpired && (
                              <span className="text-xs text-amber-600 font-medium">Prova gratuita — {s.trial_days_left} giorni rimasti</span>
                            )}
                            {isTrial && trialExpired && (
                              <span className="text-xs text-red-600 font-medium">Prova scaduta — attivazione necessaria</span>
                            )}
                            {!isTrial && isPaid && (
                              <span className="text-xs text-emerald-600">Attiva fino al {new Date(s.annual_paid_until).toLocaleDateString('it-IT')}</span>
                            )}
                            {!isTrial && isExpired && (
                              <span className="text-xs text-red-600">Canone scaduto — rinnovo necessario</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div>
                        {isTrial ? (
                          <button onClick={() => handleActivate(s.clinic_id, s.clinic_name)} disabled={activateLoading === s.clinic_id}
                            className="px-4 py-2 bg-sky-600 text-white text-sm rounded-lg hover:bg-sky-700 disabled:opacity-50" data-testid={`activate-${s.clinic_id}`}>
                            {activateLoading === s.clinic_id ? 'Attivazione...' : 'Attiva (€300/anno)'}
                          </button>
                        ) : isExpired ? (
                          <button onClick={() => handleRenew(s.clinic_id, s.clinic_name)} disabled={activateLoading === s.clinic_id}
                            className="px-4 py-2 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 disabled:opacity-50" data-testid={`renew-${s.clinic_id}`}>
                            {activateLoading === s.clinic_id ? 'Rinnovo...' : 'Rinnova (€300/anno)'}
                          </button>
                        ) : (
                          <span className="px-3 py-1.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">Attiva</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Transactions History */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="transactions-list">
            <div className="p-5 border-b border-slate-100">
              <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                <Clock className="w-5 h-5 text-sky-600" /> Storico Movimenti
              </h2>
            </div>
            <div className="divide-y divide-slate-100">
              {transactions.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <p className="text-sm">Nessun movimento registrato</p>
                </div>
              ) : transactions.map((t, i) => (
                <div key={i} className="flex items-center gap-4 p-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${t.type === 'voice_call' ? 'bg-violet-100' : t.type === 'clinic_setup' ? 'bg-sky-100' : 'bg-emerald-100'}`}>
                    {t.type === 'voice_call' ? <Phone className="w-4 h-4 text-violet-600" /> : t.type === 'clinic_setup' ? <Building2 className="w-4 h-4 text-sky-600" /> : <ArrowDownRight className="w-4 h-4 text-emerald-600" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">{t.description}</p>
                    <p className="text-xs text-slate-400">{t.created_at ? new Date(t.created_at).toLocaleString('it-IT') : ''}</p>
                  </div>
                  <span className="font-semibold text-red-600">-€{t.amount?.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </Layout>
  );
}
